import { z } from 'zod';

const STORAGE_KEY = 'air-premiere:clip-imports';

export const CLIP_IMPORT_RECORD_CHANGED_EVENT = 'air-premiere:clip-import-changed';

const ClipImportKindSchema = z.enum(['original', 'proxy']);

export type ClipImportKind = z.infer<typeof ClipImportKindSchema>;

const ClipImportRecordSchema = z.object({
  altResId: z.string().optional(),
  assetId: z.string(),
  clipId: z.string(),
  fileName: z.string(),
  importedAt: z.number(),
  kind: ClipImportKindSchema,
});

export type ClipImportRecord = z.infer<typeof ClipImportRecordSchema>;

const ClipImportMapSchema = z.record(z.string(), ClipImportRecordSchema);

type ClipImportMap = z.infer<typeof ClipImportMapSchema>;

const notifyChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CLIP_IMPORT_RECORD_CHANGED_EVENT));
  }
};

const readRecords = (): ClipImportMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return {};
    }

    const parsed = ClipImportMapSchema.safeParse(JSON.parse(raw));

    if (!parsed.success) {
      localStorage.removeItem(STORAGE_KEY);

      return {};
    }

    return parsed.data;
  } catch {
    localStorage.removeItem(STORAGE_KEY);

    return {};
  }
};

const writeRecords = (records: ClipImportMap) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    // localStorage may be unavailable — swallow so import success is not blocked.
  }
};

export type RecordClipImportParams = {
  altResId?: string;
  assetId: string;
  clipId: string;
  fileName: string;
  kind: ClipImportKind;
};

/** Persists the latest successful Premiere import for an Air clip (original or proxy). */
export const recordClipImport = ({ altResId, assetId, clipId, fileName, kind }: RecordClipImportParams) => {
  const records = readRecords();
  records[clipId] = {
    altResId,
    assetId,
    clipId,
    fileName,
    importedAt: Date.now(),
    kind,
  };
  writeRecords(records);
  notifyChanged();
};

export const getClipImportRecord = (clipId: string): ClipImportRecord | null => {
  const records = readRecords();

  return records[clipId] ?? null;
};
